import React, { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { useHotIssues } from '../hooks/useHotIssues';
import {
  closedYear,
  emptyHotIssueForm,
  formatIssueDate,
  formatIssueDateShort,
  formatUpdateWhen,
  hotIssueToForm,
  isClosedHotIssue,
  notesNewestFirst,
  PRIORITIES,
  sortActiveHotIssues,
  statusChoices,
} from '../utils/hotIssues';
import './HotIssues.css';

function IssueFields({ form, setForm, idPrefix, showExtras }) {
  return (
    <>
      <div className="form-group">
        <label htmlFor={`${idPrefix}-title`}>Title</label>
        <input
          id={`${idPrefix}-title`}
          type="text"
          value={form.issue}
          onChange={(e) => setForm({ ...form, issue: e.target.value })}
          placeholder="What needs to be discussed?"
          required
          maxLength={300}
          autoComplete="off"
        />
      </div>
      <div className="form-group">
        <label htmlFor={`${idPrefix}-details`}>Description</label>
        <textarea
          id={`${idPrefix}-details`}
          value={form.details}
          onChange={(e) => setForm({ ...form, details: e.target.value })}
          placeholder="Optional details"
          rows={3}
          maxLength={4000}
        />
      </div>
      <div className="hot-issue-form-row">
        <div className="form-group">
          <label htmlFor={`${idPrefix}-owner`}>Owner</label>
          <input
            id={`${idPrefix}-owner`}
            type="text"
            value={form.poc}
            onChange={(e) => setForm({ ...form, poc: e.target.value })}
            placeholder="Who is carrying this?"
            maxLength={200}
            autoComplete="off"
          />
        </div>
        <div className="form-group">
          <label htmlFor={`${idPrefix}-priority`}>Priority</label>
          <select
            id={`${idPrefix}-priority`}
            value={form.priority}
            onChange={(e) => setForm({ ...form, priority: e.target.value })}
          >
            <option value="">No priority</option>
            {PRIORITIES.map((priority) => (
              <option key={priority} value={priority}>{priority}</option>
            ))}
          </select>
        </div>
      </div>
      {showExtras && (
        <div className="hot-issue-form-extra">
          <div className="form-group">
            <label htmlFor={`${idPrefix}-date`}>Date</label>
            <input
              id={`${idPrefix}-date`}
              type="date"
              value={form.date}
              onChange={(e) => setForm({ ...form, date: e.target.value })}
              required
            />
          </div>
          <div className="form-group">
            <label htmlFor={`${idPrefix}-personnel`}>Personnel</label>
            <input
              id={`${idPrefix}-personnel`}
              type="text"
              value={form.personnel}
              onChange={(e) => setForm({ ...form, personnel: e.target.value })}
              placeholder="Personnel"
              maxLength={200}
            />
          </div>
          <div className="form-group">
            <label htmlFor={`${idPrefix}-next`}>Next step</label>
            <input
              id={`${idPrefix}-next`}
              type="text"
              value={form.nextStep}
              onChange={(e) => setForm({ ...form, nextStep: e.target.value })}
              placeholder="Next step"
              maxLength={500}
            />
          </div>
        </div>
      )}
    </>
  );
}

function IssueForm({
  initial,
  idPrefix,
  submitLabel,
  submitting,
  showExtras,
  onSubmit,
  onCancel,
}) {
  const [form, setForm] = useState(initial);
  const [more, setMore] = useState(showExtras || Boolean(initial.personnel || initial.nextStep));

  const handleSubmit = (e) => {
    e.preventDefault();
    onSubmit({
      ...form,
      issue: form.issue.trim(),
      details: form.details.trim(),
      poc: form.poc.trim(),
      personnel: form.personnel.trim(),
      nextStep: form.nextStep.trim(),
    });
  };

  return (
    <form onSubmit={handleSubmit} className="hot-issue-form">
      <IssueFields form={form} setForm={setForm} idPrefix={idPrefix} showExtras={showExtras || more} />
      {!showExtras && (
        <button type="button" className="meetings-manage-toggle hot-issue-more" onClick={() => setMore((open) => !open)}>
          {more ? '▼' : '▶'} More details
        </button>
      )}
      <div className="hot-issue-form-actions">
        <button type="submit" className="hot-issue-primary" disabled={submitting}>
          {submitting ? 'Saving…' : submitLabel}
        </button>
        {onCancel && (
          <button type="button" onClick={onCancel}>Cancel</button>
        )}
      </div>
    </form>
  );
}

function IssueCard({ item, statusOptions, submitting, onUpdate, onStatus, onDelete, onAddNote, onAddStatus }) {
  const [editing, setEditing] = useState(false);
  const [confirmingDelete, setConfirmingDelete] = useState(false);
  const [note, setNote] = useState('');
  const choices = statusChoices(item, statusOptions);
  const notes = notesNewestFirst(item.updates);

  const saveEdit = async (payload) => {
    const ok = await onUpdate(item._id, payload);
    if (ok) setEditing(false);
  };

  const saveNote = async (e) => {
    e.preventDefault();
    const ok = await onAddNote(item, note);
    if (ok) setNote('');
  };

  return (
    <article className={`hot-issue-card hot-issue-priority-${(item.priority || 'none').toLowerCase()}`}>
      <header className="hot-issue-card-head">
        <div>
          {item.priority ? <span className="hot-issue-priority">{item.priority}</span> : null}
          <h3 className="hot-issue-title">{item.issue || 'Untitled issue'}</h3>
        </div>
        <time className="hot-issue-date" dateTime={item.date}>{formatIssueDateShort(item.date)}</time>
      </header>

      {editing ? (
        <IssueForm
          initial={hotIssueToForm(item)}
          idPrefix={`hot-issue-edit-${item._id}`}
          submitLabel="Save changes"
          submitting={submitting}
          showExtras
          onSubmit={saveEdit}
          onCancel={() => setEditing(false)}
        />
      ) : (
        <div className="hot-issue-body">
          {item.details ? <p className="hot-issue-details">{item.details}</p> : null}
          <dl className="hot-issue-meta">
            {item.poc ? (
              <div>
                <dt>Owner</dt>
                <dd>{item.poc}</dd>
              </div>
            ) : null}
            {item.personnel ? (
              <div>
                <dt>Personnel</dt>
                <dd>{item.personnel}</dd>
              </div>
            ) : null}
            {item.nextStep ? (
              <div>
                <dt>Next step</dt>
                <dd>{item.nextStep}</dd>
              </div>
            ) : null}
          </dl>
          <div className="hot-issue-card-actions">
            <button type="button" onClick={() => setEditing(true)}>Edit</button>
          </div>
        </div>
      )}

      {confirmingDelete ? (
        <div className="hot-issue-confirm" role="alertdialog" aria-label="Delete this issue? This can't be undone.">
          <p>Delete this issue? This can't be undone.</p>
          <div className="hot-issue-confirm-actions">
            <button type="button" onClick={() => setConfirmingDelete(false)} disabled={submitting}>Cancel</button>
            <button
              type="button"
              className="hot-issue-delete"
              disabled={submitting}
              onClick={async () => {
                const ok = await onDelete(item);
                if (!ok) setConfirmingDelete(false);
              }}
            >
              {submitting ? 'Deleting…' : 'Delete issue'}
            </button>
          </div>
        </div>
      ) : (
        <div className="hot-issue-card-actions">
          <button type="button" className="hot-issue-delete" onClick={() => setConfirmingDelete(true)}>
            Delete
          </button>
        </div>
      )}

      <div className="hot-issue-status" role="group" aria-label={`Status for ${item.issue || 'issue'}`}>
        {choices.map((status) => (
          <button
            key={status}
            type="button"
            className={`hot-issue-status-btn${item.status === status ? ' is-current' : ''}`}
            aria-pressed={item.status === status}
            disabled={submitting || item.status === status}
            onClick={() => onStatus(item, status)}
          >
            {status}
          </button>
        ))}
        <button
          type="button"
          className="hot-issue-status-btn hot-issue-status-add"
          onClick={() => onAddStatus((value) => onStatus(item, value))}
          disabled={submitting}
        >
          + Status
        </button>
      </div>

      <div className="hot-issue-notes">
        <h4>Notes</h4>
        {notes.length === 0 ? (
          <p className="hot-issue-notes-empty">No notes yet.</p>
        ) : (
          <ol className="hot-issue-notes-list">
            {notes.map((entry) => (
              <li key={entry._id || `${entry.createdAt}-${entry.text}`}>
                <p className="hot-issue-note-text">{entry.text}</p>
                <p className="hot-issue-note-meta">
                  {entry.authorName || 'Ministry lead'}
                  {entry.createdAt ? ` · ${formatUpdateWhen(entry.createdAt)}` : ''}
                </p>
              </li>
            ))}
          </ol>
        )}
        <form onSubmit={saveNote} className="hot-issue-note-form">
          <label className="sr-only" htmlFor={`hot-issue-note-${item._id}`}>Add a note</label>
          <textarea
            id={`hot-issue-note-${item._id}`}
            value={note}
            onChange={(e) => setNote(e.target.value)}
            placeholder="Add a note"
            rows={2}
            maxLength={2000}
          />
          <button type="submit" disabled={submitting || !note.trim()}>Add note</button>
        </form>
      </div>
    </article>
  );
}

export function HotIssuesWorkspace({
  api,
  addOpen,
  onToggleAdd,
  showAddToggle = true,
  autoFocusTitle = false,
}) {
  const [listView, setListView] = useState('active');
  const [newFormKey, setNewFormKey] = useState(0);
  const active = useMemo(
    () => sortActiveHotIssues(api.issues.filter((issue) => !isClosedHotIssue(issue))),
    [api.issues]
  );
  const closed = useMemo(
    () => api.issues.filter(isClosedHotIssue),
    [api.issues]
  );
  const closedByYear = useMemo(() => {
    const groups = {};
    closed.forEach((issue) => {
      const year = closedYear(issue);
      if (!groups[year]) groups[year] = [];
      groups[year].push(issue);
    });
    return Object.keys(groups)
      .map(Number)
      .sort((a, b) => b - a)
      .map((year) => ({ year, issues: groups[year] }));
  }, [closed]);

  useEffect(() => {
    if (!autoFocusTitle || !addOpen) return;
    const field = document.getElementById('hot-issue-new-title');
    if (field) field.focus();
  }, [addOpen, autoFocusTitle, newFormKey]);

  const meetingLine = api.nextMeeting
    ? `Next Ministry Lead meeting: ${formatIssueDate(api.nextMeeting.date)}. `
    : 'These issues are for the next Ministry Lead meeting. ';

  return (
    <div className="hot-issues-workspace">
      <p className="hot-issue-carry">
        {meetingLine}
        Open items stay on this list until they are resolved, including ones carried over from earlier weeks.
      </p>
      {api.error ? <p className="meetings-error" role="alert">{api.error}</p> : null}
      {api.notice ? <p className="hot-issue-notice" role="status">{api.notice}</p> : null}

      {showAddToggle && (
        <button
          type="button"
          className="meetings-manage-toggle meetings-hot-issues-add-toggle"
          onClick={onToggleAdd}
        >
          {addOpen ? '▼' : '▶'} Add Issue to Discuss
        </button>
      )}

      {addOpen && (
        <IssueForm
          key={newFormKey}
          initial={emptyHotIssueForm()}
          idPrefix="hot-issue-new"
          submitLabel="Save issue"
          submitting={api.submitting}
          onSubmit={async (payload) => {
            const ok = await api.addIssue({ ...payload, status: payload.status || 'Open' });
            if (!ok) return;
            setNewFormKey((value) => value + 1);
            if (onToggleAdd && showAddToggle) onToggleAdd();
          }}
        />
      )}

      <div className="hot-issue-view-toggle" role="group" aria-label="Issue list">
        <button
          type="button"
          aria-pressed={listView === 'active'}
          className={listView === 'active' ? 'is-current' : ''}
          onClick={() => setListView('active')}
        >
          Active ({active.length})
        </button>
        <button
          type="button"
          aria-pressed={listView === 'closed'}
          className={listView === 'closed' ? 'is-current' : ''}
          onClick={() => setListView('closed')}
        >
          Closed ({closed.length})
        </button>
      </div>

      {listView === 'active' && active.length === 0 && (
        <p className="meetings-empty">No open issues yet.</p>
      )}

      {listView === 'active' && active.map((item) => (
        <IssueCard
          key={item._id}
          item={item}
          statusOptions={api.statusOptions}
          submitting={api.submitting}
          onUpdate={api.updateIssue}
          onStatus={api.setStatus}
          onDelete={api.removeIssue}
          onAddNote={api.addNote}
          onAddStatus={api.addStatusOption}
        />
      ))}

      {listView === 'closed' && closed.length === 0 && (
        <p className="meetings-empty">No closed issues.</p>
      )}

      {listView === 'closed' && closedByYear.map(({ year, issues }) => (
        <section key={year} className="hot-issue-year">
          <h3>{year}</h3>
          {issues.map((item) => (
            <IssueCard
              key={item._id}
              item={item}
              statusOptions={api.statusOptions}
              submitting={api.submitting}
              onUpdate={api.updateIssue}
              onStatus={api.setStatus}
              onDelete={api.removeIssue}
              onAddNote={api.addNote}
              onAddStatus={api.addStatusOption}
            />
          ))}
        </section>
      ))}
    </div>
  );
}

const HotIssuesSection = ({ events }) => {
  const [open, setOpen] = useState(false);
  const [addOpen, setAddOpen] = useState(false);
  const api = useHotIssues(events);
  const activeCount = api.issues.filter((issue) => !isClosedHotIssue(issue)).length;

  const openAdd = () => {
    setOpen(true);
    setAddOpen(true);
  };

  return (
    <section className="meetings-manage-section meetings-hot-issues-section">
      <div className="meetings-hot-issues-heading">
        <button
          type="button"
          className="meetings-manage-toggle"
          onClick={() => setOpen((value) => !value)}
          aria-expanded={open}
        >
          {open ? '▼' : '▶'} M Issues to Discuss ({activeCount})
        </button>
        <button type="button" className="hot-issue-quick-add" onClick={openAdd}>
          Add issue
        </button>
        <Link to="/admin/meetings/issues" className="hot-issue-phone-link">
          Add from your phone
        </Link>
      </div>
      {open && (
        <div className="meetings-hot-issues-content">
          <HotIssuesWorkspace
            api={api}
            addOpen={addOpen}
            autoFocusTitle={addOpen}
            onToggleAdd={() => setAddOpen((value) => !value)}
          />
        </div>
      )}
    </section>
  );
};

export default HotIssuesSection;
